/**
 * Quiz scoring. Mirrors src/functions/em-quiz/scoring.php; both are checked
 * against tests/em-quiz/fixtures.json.
 *
 * Kept as .mjs with no imports so Node can run the tests against it directly.
 *
 * @package ppd
 * @since   1.0.27
 */

const round = (n) => Math.round(n * 1e4) / 1e4;

/**
 * The outcomes in `pool` with the highest value in `totals`, in registry order.
 *
 * @param {string[]}               pool
 * @param {Object<string, number>} totals
 * @return {string[]}
 */
function leaders(pool, totals) {
	const best = Math.max(...pool.map((key) => totals[key]));
	return pool.filter((key) => totals[key] === best);
}

/**
 * Score a set of answers. See em_quiz_score() for the rules.
 *
 * @param {Array<string|{category: string, weight?: number}>} answers One entry per
 *        question, in question order: a category key, or { category, weight }
 *        (weight defaults to 1).
 * @param {Object}   rules
 * @param {Object}   rules.categories Category key => outcome key.
 * @param {string[]} rules.outcomes   Outcome keys, in registry order.
 * @param {string}   rules.fallback   Outcome used when no tie-break settles it.
 * @return {{scores: Object<string, number>, weighted: Object<string, number>, outcome: string, step: string}}
 */
export function score(answers, { categories, outcomes, fallback }) {
	const scores = {};
	const weighted = {};
	outcomes.forEach((key) => {
		scores[key] = 0;
		weighted[key] = 0;
	});

	const picks = [];
	answers.forEach((answer, index) => {
		const category = typeof answer === 'string' ? answer : answer.category;
		const weight = typeof answer === 'object' && answer.weight !== undefined
			? Math.max(0, Number(answer.weight) || 0)
			: 1;
		const outcome = categories[category];
		if (!(outcome in scores) || !weight) {
			return;
		}
		scores[outcome] += 1;
		weighted[outcome] += weight;
		picks.push({ outcome, weight, index });
	});
	outcomes.forEach((key) => {
		weighted[key] = round(weighted[key]);
	});

	const result = (outcome, step) => ({
		scores, weighted, outcome, step,
	});

	// 1. Most answers wins.
	let tied = leaders(outcomes, scores);
	if (tied.length === 1) {
		return result(tied[0], 'majority');
	}

	// 2. Highest weighted total among the tied outcomes.
	tied = leaders(tied, weighted);
	if (tied.length === 1) {
		return result(tied[0], 'weighted');
	}

	// 3. Questions weighted above 1, heaviest first: the first answer that
	// belongs to a still-tied outcome wins.
	const deciders = picks
		.filter((pick) => pick.weight > 1)
		.sort((a, b) => b.weight - a.weight || a.index - b.index);
	const decider = deciders.find((pick) => tied.includes(pick.outcome));
	if (decider) {
		return result(decider.outcome, `q${decider.index + 1}`);
	}

	// 4. Nothing settles it.
	return result(fallback && fallback in scores ? fallback : tied[0], 'fallback');
}

/**
 * Format scores for HubSpot, e.g. "fsp:4|fso:1|mixed:1".
 *
 * @param {Object<string, number>} scores
 * @return {string}
 */
export function formatScores(scores) {
	return Object.keys(scores).map((key) => `${key}:${scores[key]}`).join('|');
}
